/**
 * Dev fixture for manual verification of #1330-5 / #1330-6.
 *
 * Web: append `?fpAttachmentFixture=1` to the Request Detail URL while running
 * `yarn web` in development. Attachments merge into the live query result.
 */

import { SECTION_IDS } from '../constants/sectionIds';

import type { SavedAttachment } from '../services/attachmentService';

let fixtureAttachmentId = 9_133_000;

function nextFixtureAttachmentId(): number {
  fixtureAttachmentId += 1;
  return fixtureAttachmentId;
}

export const FACILITY_POWER_FAMILY_SCOPE_FIXTURE = [
  'opportunity',
  'facility-power',
  'large-ups',
  'power-distribution',
  'documents',
  'summary',
] as const;

export function createFacilityPowerFamilyAttachmentsFixture(
  requestId: number,
): Record<string, SavedAttachment[]> {
  const mk = (
    attachmentName: string,
    requestSection: string,
    captions: string,
  ): SavedAttachment => ({
    id: nextFixtureAttachmentId(),
    requestId,
    attachmentName,
    requestSection,
    captions,
    mimeType: 'application/pdf',
    createdAt: '2026-06-26T12:00:00.000Z',
  });

  return {
    [SECTION_IDS.FACILITY_POWER]: [
      mk(
        'facility-power-readiness-one-line.pdf',
        SECTION_IDS.FACILITY_POWER,
        'Mock — Facility Power Readiness subsection',
      ),
    ],
    [SECTION_IDS.FACILITY_POWER_LARGE_UPS]: [
      mk(
        'large-ups-battery-schedule.pdf',
        SECTION_IDS.FACILITY_POWER_LARGE_UPS,
        'Mock — Large UPS subsection',
      ),
    ],
    [SECTION_IDS.FACILITY_POWER_DISTRIBUTION]: [
      mk(
        'power-distribution-busway.pdf',
        SECTION_IDS.FACILITY_POWER_DISTRIBUTION,
        'Mock — Power Distribution subsection',
      ),
    ],
  };
}

export function isFacilityPowerAttachmentFixtureEnabled(): boolean {
  if (typeof __DEV__ === 'undefined' || !__DEV__) {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('fpAttachmentFixture') === '1';
  } catch {
    return false;
  }
}

export function mergeFacilityPowerAttachmentFixture(
  attachmentsBySection: Record<string, SavedAttachment[]>,
  requestId: number,
): Record<string, SavedAttachment[]> {
  if (!isFacilityPowerAttachmentFixtureEnabled()) {
    return attachmentsBySection;
  }

  const fixture = createFacilityPowerFamilyAttachmentsFixture(
    Number.parseInt(String(requestId), 10) || 1330,
  );
  const merged: Record<string, SavedAttachment[]> = {
    ...attachmentsBySection,
  };

  for (const [sectionId, attachments] of Object.entries(fixture)) {
    merged[sectionId] = [...(merged[sectionId] ?? []), ...attachments];
  }

  return merged;
}
